























































































































import EntrySearch from "@/components/EntrySearch.vue";
import { Component, Mixins, Ref, Watch } from "vue-property-decorator";
import InvestorMixin from "@/mixins/http/Investor";
import TimeDistribution from "@/components/TimeDistribution.vue";
import MediaSelect from "@/components/MediaSelect.vue";
import VSwitchButton from "@/components/VSwitchButton.vue";
import SpotList, { SpotFilter } from "@/components/Spot/List.vue";
import DatePicker, { DATE_FORMAT } from "@/components/DatePicker.vue";
import { Getter } from "vuex-class";
import MediaValue from "@/interfaces/MediaValue";
import { Media, Product, Spot, Target, User } from "@/models";
import AggregationMode from "@/components/Spot/AggregationMode.vue";
import Compressor from "@/services/Compressor";
import QueryHelper from "@/services/QueryHelper";
import QueryParser from "@/services/QueryParser";
import { InvestorSpotFilter } from "@/views/Investor/Creativity/CreativityDetail.vue";
import EntryMixin from "@/mixins/http/Entry";
import { FEATURE_AUDITEL } from "@/constants";
import GrpTargetSelect from "@/components/GrpTargetSelect.vue";
import Disclaimer from "@/components/GrpTargets/Disclaimer.vue";
import AdcontrolProfile from "@/models/AdcontrolProfile";

Component.registerHooks(["beforeRouteUpdate"]);

interface CreativityFilter {
  media: {
    radio: boolean;
    tv: boolean;
  };
  date: {
    to: string;
    from: string;
  };
  product: string;
}

@Component({
  components: {
    SpotList,
    TimeDistribution,
    EntrySearch,
    DatePicker,
    MediaSelect,
    VSwitchButton,
    AggregationMode,
    GrpTargetSelect,
    Disclaimer,
  },
})
export default class Creativity extends Mixins(InvestorMixin, EntryMixin) {
  @Getter("lastDate") readonly lastDate!: string;
  @Getter("userMedia") readonly userMedia!: MediaValue;
  @Getter("user") readonly user!: User;
  @Getter("profile") readonly adcontrolProfile!: AdcontrolProfile;

  @Getter readonly parseDateValue!: (
    date: string,
    format: string,
    defaultOffset?: number,
    defaultUnit?: any
  ) => string;
  @Getter readonly parseAndCleanMediaValue!: (
    radio: string | boolean | undefined,
    tv: string | boolean | undefined
  ) => MediaValue;

  @Ref("spotList") readonly spotList!: SpotList;

  aggregationMode = false;
  ready = false;
  selected: Spot[] = [];
  search = "";
  found = false;
  currentGrpTarget: Target | null = null;
  spotTypeSwitch = true;
  showSpotTypeSwitch = false;
  unitOfMeasure = "duration";

  get aggregationModeEnabled(): boolean {
    return this.aggregationMode && this.enoughSpots;
  }

  get enoughSpots(): boolean {
    return this.spotList.spots?.length > 2;
  }

  get spotTypeDetail(): boolean {
    return this.spotTypeSwitch && this.showSpotTypeSwitch;
  }

  protected onDateSelected({ range }: { range: { from: string; to: string } }) {
    if (range) {
      this.filter.date = range;
    }
  }

  onSelected(selected: Spot[]) {
    this.selected = selected;
  }

  onSpotClicked(spot: Spot) {
    this.selected = [spot];
    this.onAggregate();
  }

  onUnitOfMeasureChanged(settings: { unitOfMeasure?: string }) {
    if (settings.unitOfMeasure) {
      this.unitOfMeasure = settings.unitOfMeasure;
    }
  }

  async onTargetSelected(target: Target) {
    this.currentGrpTarget = target;
  }

  get grpTargetId() {
    if (this.currentGrpTarget && !this.filter.media.radio)
      return this.currentGrpTarget?.id;
    else return null;
  }

  product: Product | null = null;

  filter = {
    media: {
      radio: true,
      tv: true,
    },
    date: {
      to: "",
      from: "",
    },
    product: "",
  };

  parseParamsOrDefault(param: string | undefined, defaultValue: boolean) {
    return param !== undefined ? (param == "1" ? true : false) : defaultValue;
  }

  buildFilterFromQuery(): CreativityFilter {
    const query = this.$route.query;
    const { radio, tv } = query;

    const from = this.parseDateValue(
      query.from as string,
      DATE_FORMAT,
      3,
      "months"
    );
    const to = this.parseDateValue(query.to as string, DATE_FORMAT);

    const media = this.parseAndCleanMediaValue(
      QueryParser.queryToBoolean(radio),
      QueryParser.queryToBoolean(tv)
    );

    const product = (query.product as string) ?? "";

    return {
      media,
      date: {
        from,
        to,
      },
      product,
    };
  }

  @Watch("$store.state.settings.profile")
  async onProfileUpdated() {
    this.enforceProfileMediaType();
    this.filter = { ...this.buildFilterFromQuery() };
    if (this.filter.product) {
      this.product = await this.getProduct(this.filter.product, "get_product");
    }
    this.ready = true;
  }

  @Watch("filter", { deep: true })
  onFilterChanged() {
    if (QueryHelper.isSameQuery(this.$route.query, this.queryFilter)) {
      return;
    }

    this.$router.replace({ query: { ...this.queryFilter } });
  }

  get queryFilter(): Dictionary<string> {
    return {
      from: this.filter.date.from as string,
      to: this.filter.date.to as string,
      tv: this.filter.media.tv ? "1" : "0",
      radio: this.filter.media.radio ? "1" : "0",
      product: this.filter.product as string,
    };
  }

  async created() {
    this.filter = { ...this.buildFilterFromQuery() };
    this.enforceProfileMediaType();

    if (this.filter.product) {
      this.product = await this.getProduct(this.filter.product, "get_product");
    }
    this.ready = true;
  }

  get productLabel() {
    return this.product?.name;
  }

  onAggregate() {
    const ids = this.selected.map((o) => o.id);
    const compressed = Compressor.compress(ids);
    this.$router.push({
      name: "InvestorCreativityDetail",
      params: { product_id: this.product?.id, ids: compressed },
      query: {
        ...this.convertInvestorSpotFilterToQuery(
          this.getDetailFilterFromCurrentFilter()
        ),
      },
    });
  }

  get spotTableFilter(): SpotFilter | null {
    if (!this.product || !this.filter.date.from || !this.filter.date.to) {
      return null;
    }
    const media = Media.mediaToList(this.filter.media);

    const grpTarget = this.currentGrpTarget?.id;

    const data = {
      dateRange: [this.filter.date.from, this.filter.date.to],
      product: this.product.id,
      media,
      grpTarget,
      vod: this.adcontrolProfile?.mediaSourceKind === "msk_vod" ?? false,
    };
    return data;
  }

  onProductSelected(product: Product) {
    this.product = product;
    this.filter.product = product.id;
  }

  handleFound(record: number) {
    this.found = record > 0;
    this.showSpotTypeSwitch = !!this.spotList?.spots?.find(
      (e) => e.spotType === "SPONSORIZZAZIONI"
    );
  }

  protected convertInvestorSpotFilterToQuery(
    isp: InvestorSpotFilter
  ): Dictionary<string> {
    const query = {
      from: isp.date.from as string,
      to: isp.date.to as string,
      tv: isp.media.tv ? "1" : "0",
      radio: isp.media.radio ? "1" : "0",
    };
    return query;
  }

  protected getDetailFilterFromCurrentFilter(): InvestorSpotFilter {
    return {
      media: this.filter.media,
      date: this.filter.date,
      unitOfMeasure: "spots",
      agencies: false,
    };
  }

  private enforceProfileMediaType() {
    if (this.adcontrolProfile?.accountType === "radio") {
      this.filter.media.tv = false;
      this.filter.media.radio = true;
    } else if (this.adcontrolProfile?.accountType === "tv") {
      this.filter.media.tv = true;
      this.filter.media.radio = false;
    }
  }

  get hasAuditel(): boolean {
    return this.user.hasFeature(FEATURE_AUDITEL);
  }
}
